// The account valued at the marks, once for the page: every open position's payout after the
// venue's impact curve and win fee, how near its bust, and the account's equity. Recomputed by
// the SDK as a mark or the account moves.

import { useEffect } from "react";
import { pt, type Valuation, type Valuations } from "../sdk";
import { accountState } from "./account";
import { markMap, marks, symbols } from "./market";
import { toast } from "./toasts";
import { createStore, useStore } from "./store";

export const valuations = createStore<Valuations | null>(null);

export const useValuations = () => useStore(valuations);

const EMPTY: Record<string, Valuation> = {};
const byIdStore = createStore<Record<string, Valuation>>(EMPTY);

/** One position's valuation, by id. */
export const useValuation = (positionId: string) => useStore(byIdStore, (all) => all[positionId]);

/** Positions already warned about, until they back off. */
const warned = new Set<string>();
const WARN_BP = 8_000;
const REARM_BP = 5_000;

/** Says once when a position is most of the way to its bust. */
function watchRisk(next: Valuations | null) {
  const account = accountState.get().account;
  if (!next || !account) return;
  for (const valuation of next.positions) {
    const risk = valuation.riskBp;
    if (risk === null) continue;
    if (risk < REARM_BP) warned.delete(valuation.positionId);
    if (risk < WARN_BP || warned.has(valuation.positionId) || valuation.liquidated) continue;
    warned.add(valuation.positionId);
    const position = account.positions.find((held) => held.positionId === valuation.positionId);
    if (!position) continue;
    const symbol = symbols.get(position.instrumentId) ?? "";
    toast("down", `${symbol} ${position.isLong ? "up" : "down"} ${position.leverage}× is near its bust`, `${Math.floor(risk / 100)}% of the way there. Cash out or let it ride.`);
  }
}

function recompute() {
  const { owner, account } = accountState.get();
  if (!owner || !account) {
    valuations.set(null);
    byIdStore.set(EMPTY);
    return;
  }
  try {
    const next = pt.valuations(owner, markMap(marks.get()));
    valuations.set(next);
    watchRisk(next);
    byIdStore.set(Object.fromEntries((next?.positions ?? []).map((valuation) => [valuation.positionId, valuation])));
  } catch (error) {
    console.warn("valuations", error);
  }
}

/** Keeps the valuations: mounted once by the app. */
export function useValuationFeed() {
  useEffect(() => {
    let frame = 0;
    // marks tick many times a second: value at most once a frame
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        recompute();
      });
    };
    const a = accountState.subscribe(schedule);
    const m = marks.subscribe(schedule);
    recompute();
    return () => {
      a();
      m();
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
}
